import { useCallback, useEffect } from "react";

import { useIsFetching } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";
import { useDispatch, useSelector } from "react-redux";

import Sentry from "@similarweb/sentry-logger";

import { useDidUpdate } from "app/custom-hooks/useDidUpdate";
import { useMount } from "app/custom-hooks/useMount";
import { useIsLeadsCriticalDataLoading } from "app/pages/sales-intelligence/modules/leads-search/hooks/useIsLeadsCriticalDataLoading";
import type { LeadsSearchAllFiltersTypes } from "app/pages/sales-intelligence/modules/leads-search-filters";
import {
    leadsSearchFiltersQueryKeys,
    selectFiltersPanelValue,
    selectDirtyFiltersFlag,
} from "app/pages/sales-intelligence/modules/leads-search-filters";
import { useFiltersUpdate } from "app/pages/sales-intelligence/modules/leads-search-filters/hooks/useFiltersUpdate";
import { setShouldWaitToFiltersFromSavedSearch } from "app/pages/sales-intelligence/sub-modules/opportunities/store/action-creators";
import { selectShouldSetFiltersFromSavedSearch } from "app/pages/sales-intelligence/sub-modules/opportunities/store/selectors";
import type { CrmListRoutingParams } from "app/pages/sales-intelligence/sub-modules/opportunities/types/routing";

export const useCrmListSavedSearch = () => {
    const dispatch = useDispatch();

    const { params } = useCurrentStateAndParams<CrmListRoutingParams>();

    const hasFilters = useSelector(selectDirtyFiltersFlag);
    const filtersPanelValue = useSelector(selectFiltersPanelValue);
    const shouldWaitToFiltersFromSavedSearch = useSelector(selectShouldSetFiltersFromSavedSearch);

    const filtersDescriptionFetchingCount = useIsFetching({
        queryKey: leadsSearchFiltersQueryKeys.description(),
    });
    const handleFilterUpdate = useFiltersUpdate();

    const isLeadsCriticalDataLoading = useIsLeadsCriticalDataLoading();

    const storageKey = `${params.crm}-${params.objectType}-saved-filters`;

    const loadSavedFilters = useCallback((key: string): Partial<LeadsSearchAllFiltersTypes> | null => {
        try {
            const savedData = localStorage.getItem(key);
            if (!savedData) {
                return null;
            }

            return JSON.parse(savedData) as LeadsSearchAllFiltersTypes;
        } catch (error) {
            Sentry.errorTracker({
                message: "useCrmListSavedSearch: Error loading saved filters",
                e: error,
                level: "error",
                params: {
                    extra: { key },
                },
            });
            return null;
        }
    }, []);

    const saveSavedFilters = useCallback((key: string, filters: Partial<LeadsSearchAllFiltersTypes>) => {
        try {
            localStorage.setItem(key, JSON.stringify(filters));
        } catch (error) {
            Sentry.errorTracker({
                message: "useCrmListSavedSearch: Error saving filters",
                e: error,
                level: "error",
                params: {
                    extra: { key, filtersCount: Object.keys(filters).length },
                },
            });
        }
    }, []);

    useMount(() => {
        const savedFilters = loadSavedFilters(storageKey);

        if (savedFilters && Object.keys(savedFilters).length > 0) {
            dispatch(setShouldWaitToFiltersFromSavedSearch(true));
        }
    });

    useEffect(() => {
        if (filtersDescriptionFetchingCount > 0 || !shouldWaitToFiltersFromSavedSearch) {
            return;
        }

        const savedFilters = loadSavedFilters(storageKey);
        if (savedFilters) {
            handleFilterUpdate(savedFilters);
        }

        dispatch(setShouldWaitToFiltersFromSavedSearch(false));
    }, [filtersDescriptionFetchingCount, isLeadsCriticalDataLoading]);

    useDidUpdate(() => {
        const shouldSkipSaving = isLeadsCriticalDataLoading || !hasFilters || shouldWaitToFiltersFromSavedSearch;

        if (shouldSkipSaving) {
            return;
        }

        saveSavedFilters(storageKey, filtersPanelValue);
    }, [isLeadsCriticalDataLoading, hasFilters, filtersPanelValue, shouldWaitToFiltersFromSavedSearch]);
};
